/* eslint-disable max-classes-per-file */

import { captureException } from "@sentry/nextjs";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import UserError from "utils/UserError";
import { FileUploadError, FileUploadWrappedError } from "./errors";

if (!process.browser) {
  // 'Polyfill' Event in SSR
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  global.Event = Object;

  // 'Polyfill' EventTarget in SSR
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  global.EventTarget = Object;
}

class FileUploadProgressEvent extends Event {
  progressEvent: ProgressEvent;

  constructor(progressEvent: ProgressEvent) {
    super("uploadProgress");
    this.progressEvent = progressEvent;
  }

  get percentComplete() {
    const {
      lengthComputable: isLengthComputable,
      loaded,
      total,
    } = this.progressEvent;
    if (!isLengthComputable || !total) return;
    return loaded / total;
  }
}

type StartUploadOptions = {
  data?: unknown;
  headers?: { [header: string]: string };
  method?: string;
};

export default class FileUpload extends EventTarget {
  id: string;

  file: File;

  xhr: XMLHttpRequest;

  isUploading = false;

  lastUploadParams: { url: string; options?: StartUploadOptions } | undefined;

  constructor(file: File, id?: string) {
    super();
    this.id = id || uuidv4();
    this.file = file;
    this.xhr = new XMLHttpRequest();
  }

  startUpload(url: string, options: StartUploadOptions = {}) {
    this.lastUploadParams = { url, options };
    // eslint-disable-next-line @typescript-eslint/no-this-alias
    const fileUpload = this;

    const { data, headers = {}, method = "POST" } = options;

    this.isUploading = true;
    fileUpload.dispatchEvent(new Event("startUpload"));

    this.xhr.upload.onprogress = function handleProgress(evt) {
      fileUpload.dispatchEvent(new FileUploadProgressEvent(evt));
    };

    this.xhr.open(method, url);

    Object.keys(headers).forEach((header) => {
      this.xhr.setRequestHeader(header, headers[header]);
    });

    this.xhr.onreadystatechange = function handleReadyStateChange() {
      if (this.readyState === XMLHttpRequest.DONE) {
        fileUpload.isUploading = false;
        fileUpload.dispatchEvent(new Event("done"));
      }
    };

    this.xhr.send(this.getBody(data));

    return new Promise((resolve, reject) => {
      fileUpload.addEventListener("done", () => {
        if (fileUpload.isDone && fileUpload.isSuccess) {
          resolve(fileUpload);
        } else {
          reject(
            this.fileUploadError(
              new UserError(
                `Received ${fileUpload.xhr.status} status code from upload`,
              ),
            ),
          );
        }
      });
    });
  }

  retryUpload() {
    if (!this.lastUploadParams) {
      throw new Error("No previous upload to retry");
    }
    const { url, options } = this.lastUploadParams;

    return this.startUpload(url, options);
  }

  get responseBody() {
    if (!this.isDone) return;
    const { responseText } = this.xhr;
    if (!responseText) return;
    try {
      return JSON.parse(responseText) as unknown;
    } catch (e) {
      console.error(e);
    }
  }

  get isDone() {
    return this.xhr.readyState === XMLHttpRequest.DONE;
  }

  get isSuccess() {
    return this.xhr.status >= 200 && this.xhr.status < 300;
  }

  get hasError() {
    return this.xhr.status >= 400;
  }

  private getBody(data?: unknown) {
    if (data) return this.getFormData();
    return this.file;
  }

  private getFormData(data?: unknown) {
    const formData = new FormData();
    formData.append("file", this.file);
    if (data) formData.append("data", JSON.stringify(data));
    return formData;
  }

  protected fileUploadError(message: string): FileUploadError;
  protected fileUploadError<TError extends Error>(
    error: TError,
  ): FileUploadWrappedError<TError>;
  protected fileUploadError(messageOrError: Error | string) {
    const error =
      typeof messageOrError === "string"
        ? new FileUploadError(messageOrError, this)
        : new FileUploadWrappedError(messageOrError, this);

    captureException(error, { extra: this.getSentryExtra() });
    this.dispatchEvent(new ErrorEvent("error", { error }));
    return error;
  }

  getSentryExtra() {
    const { name, size, type } = this.file;
    return { file: { name, size, type }, xhrStatus: this.xhr.status };
  }
}

type RegisterFileUploadListenersOptions = {
  onIsLoading?: (isLoading: boolean) => void;
  onIsDone?: (isDone: boolean) => void;
  onProgress?: (progress: number | undefined) => void;
  onError?: (error: Error | undefined) => void;
};

function registerFileUploadListeners(
  upload: FileUpload,
  {
    onIsLoading,
    onIsDone,
    onProgress,
    onError,
  }: RegisterFileUploadListenersOptions,
) {
  if (onIsLoading || onError) {
    upload.addEventListener("startUpload", () => {
      onIsLoading?.(true);
      onError?.(undefined);
    });
  }

  if (onProgress) {
    upload.addEventListener("uploadProgress", (evt) => {
      if (!(evt instanceof FileUploadProgressEvent)) return;
      onProgress(evt.percentComplete);
    });
  }

  if (onIsDone || onIsLoading || onError) {
    upload.addEventListener("done", () => {
      onIsDone?.(true);
      onIsLoading?.(false);
      onError?.(undefined);
    });
  }

  if (onIsLoading || onError) {
    upload.addEventListener("error", (evt) => {
      onIsLoading?.(false);
      if (evt instanceof ErrorEvent && evt.error instanceof Error) {
        onError?.(evt.error);
      }
    });
  }
}

export function useFileUpload(upload: FileUpload) {
  const [loading, setLoading] = useState(upload.isUploading);
  const [isDone, setIsDone] = useState(false);
  const [progress, setProgress] = useState<number>();
  const [error, setError] = useState<Error>();

  registerFileUploadListeners(upload, {
    onIsLoading: setLoading,
    onIsDone: setIsDone,
    onProgress: setProgress,
    onError: setError,
  });

  return { loading, isDone, progress, error };
}

type ByIdHash<T> = { [id: UUID]: T | undefined };

function makeSetInByIdHash(id: UUID) {
  return function setInByIdHash<TValue>(
    setState: Dispatch<SetStateAction<ByIdHash<TValue>>>,
  ) {
    return function handleChange(value: TValue | undefined) {
      setState((existing) => ({ ...existing, [id]: value }));
    };
  };
}

export function useMultiFileUpload(uploads: FileUpload[]) {
  const listenersAddedRef = useRef<UUID[]>([]);

  const [loadingById, setLoadingById] = useState<ByIdHash<boolean>>({});
  const [isDoneById, setIsDoneById] = useState<ByIdHash<boolean>>({});
  const [errorById, setErrorById] = useState<ByIdHash<Error>>({});

  useEffect(() => {
    uploads.forEach((upload) => {
      if (!listenersAddedRef.current.includes(upload.id)) {
        listenersAddedRef.current.push(upload.id);
        const setInByIdHash = makeSetInByIdHash(upload.id);
        registerFileUploadListeners(upload, {
          onIsLoading: setInByIdHash(setLoadingById),
          onIsDone: setInByIdHash(setIsDoneById),
          onError: setInByIdHash(setErrorById),
        });
      }
    });
  }, [uploads]);

  return { loadingById, isDoneById, errorById };
}
